import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PrimaryBlueColor } from '../../helper/theme';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Form, Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import useAuth from '../../context/AuthProvider';
import { UsersModel, UsersModel_VS } from '../../models/UsersModel';
import { apiClient } from '../../helper/api';
import { FMTextField } from '../Elements/input';
import { FMSelectRolesCustomcopy } from './FMSelectRolesCustomcopy';

interface Iprops {
  UsersBL: any; // API user object (camelCase, includes userPartners[])
  handleClose: () => void;
  Open: boolean;
  handleSubmit: () => void;
  initValues: UsersModel;
  setSelectedOption: React.Dispatch<React.SetStateAction<string | string[]>>;
  selectedOptions: string[] | string;
  setSelectedRoles: React.Dispatch<React.SetStateAction<string | string[]>>;
  SelectedRoles: string[] | string;
  ClientUserRoles: string[];
}

const toFormModel = (u: any, fallback: UsersModel): UsersModel => {
  if (!u) return fallback;
  return {
    ...fallback,
    userId: u.userId || 0,
    firstName: u.firstName || '',
    lastName: u.lastName || '',
    userName: u.userName || '',
    userEmail: u.userEmail || '',
    roleIds: u.roleIds || '', // CSV
    isSIPAccess: !!u.isSIPAccess,
    isActive: u.isActive || '',
    partnersIds: (u.userPartners || [])
      .map((p: any) => String(p.partnersId ?? p.PartnersId))
      .filter(Boolean)
      .join(',')
  };
};

type PartnerItem = { partnersId: string; customerName: string };
type RoleItem = { roleId: number; roleName: string; isCustomerRole?: boolean };

const selectFieldSx = {
  width: '100%',
  '& .MuiSelect-select': { padding: '14.5px 16px' },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '2px solid #e2e8f0 !important',
    borderRadius: '8px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.87) !important',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#1976d2 !important',
  },
};
const textFieldSx = {
  '& .MuiOutlinedInput-notchedOutline': {
    border: '2px solid #e2e8f0 !important',
    borderRadius: '8px !important',
  },
  '& .MuiInputBase-input': { padding: '5px 14px !important' },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.87) !important',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#1976d2 !important',
  },
};
const sectionTitleSx = {
  color: 'text.secondary',
  fontWeight: 600,
  letterSpacing: '0.08em',
  display: 'block',
  mb: 1,
};
const paperSectionSx = {
  p: 2,
  borderRadius: 2,
  borderColor: 'divider',
  backgroundColor: 'grey.50',
};

export const AddEditCustomerUsers: React.FC<Iprops> = ({
  handleClose,
  Open,
  UsersBL,
  handleSubmit,
  initValues,
  selectedOptions,
  setSelectedOption,
  SelectedRoles,
  setSelectedRoles,
  ClientUserRoles
}) => {
  const { user } = useAuth();
  const token = user?.token;
  const theme = useTheme();

  const initialValues: UsersModel = useMemo(
    () => toFormModel(UsersBL, initValues),
    [UsersBL, initValues]
  );

  const [SipAccess, SetSipAccess] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [selectedMultiPartners, setSelectedMultiPartners] = useState<string[]>([]);
  const [Partner, setpartnerData] = useState<PartnerItem[]>([]);
  const [Roles, setRoles] = useState<RoleItem[]>([{ roleId: 0, roleName: '' }]);
  const [loading, setloading] = useState(false);
  const [diligentSearchPartnerList, setDiligentSearchPartnerList] = useState<PartnerItem[]>([]);
  const [notificationPrefs, setNotificationPrefs] = useState<{
    tagAlerts: boolean;
    signatureRequests: boolean;
    stampedDocuments: boolean;
    diligentSearch: boolean;
    pastDueTransactions: boolean;
    showDiligentSearchToggle: boolean;
  } | null>(null);

  const selectedPartnerNames = selectedMultiPartners.map((value: string) => {
    const userPartnerEntry = (UsersBL?.userPartners || []).find((x: any) =>
      String(x.partnersId ?? x.PartnersId) === value
    );
    const entry = Partner.find(x => x.partnersId === value);
    const partnerName =
      userPartnerEntry?.customerName ??
      userPartnerEntry?.CustomerName ??
      entry?.customerName ??
      value;
    return partnerName;
  })

  const isDiligentPartner = selectedPartnerNames.length > 0 &&
    selectedPartnerNames.every(name => diligentSearchPartnerList.some(dsp => dsp.customerName === name));

  const userRolesArr = useMemo(
    () => (user?.userRoles || '').split(',').map(r => r.trim()).filter(Boolean),
    [user?.userRoles]
  );
  const isSuperAdmin = userRolesArr.includes('Super Admin');
  const isMiscFiler = userRolesArr.includes('MISC Filer');

  const partnersDisabled =
    isSuperAdmin ? false : initialValues.roleIds === '2' ? true : isMiscFiler && initialValues.roleIds !== '5' ? false : true;

  // 1) Reset hydration guard whenever dialog opens or user changes
  const didHydrateRef = useRef(false);
  
  useEffect(() => {
    getDiligentSearchPartnerList();
  }, []);

  const getDiligentSearchPartnerList = () => {
    setloading(true);
    apiClient("/DiligentSearch/getDiligentSearchPartnerList", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    }).then((responseJson) => {
      if (!responseJson) return
      console.log('Diligent Search Partner List:', responseJson.data);
      setDiligentSearchPartnerList(responseJson.data);
    }).finally(() => {
      setloading(false);
    });
  };

  useEffect(() => {
    didHydrateRef.current = false;
  }, [Open, UsersBL?.userId]);

  // 2) Recompute selection whenever dialog opens or payload changes
  useEffect(() => {
    SetSipAccess(initialValues.isSIPAccess ?? false);
    setIsActive(initialValues.isActive ?? true);

    const idsFromUserPartners = (UsersBL?.userPartners || [])
      .map((p: any) => String(p.partnersId ?? p.PartnersId))
      .filter(Boolean);

    const idsFromCsv = String(UsersBL?.partnersIds ?? initialValues?.partnersIds ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    const idFromSingle = UsersBL?.partnersId ? [String(UsersBL.partnersId)] : [];

    const union = Array.from(new Set([...idsFromUserPartners, ...idsFromCsv, ...idFromSingle]));
    setSelectedMultiPartners(union);
  }, [Open, UsersBL?.userId, UsersBL?.partnersIds, UsersBL?.userPartners, initialValues.isSIPAccess, initialValues.isActive]);

  // 3) Hydrate from backend (userPartners) on each open (once per open)
  useEffect(() => {
    if (!Open || didHydrateRef.current) return;
    if (!UsersBL?.userId) return;

    apiClient(`/User/getById?id=${UsersBL.userId}`, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((res) => {
        if (!res || !res.data) return;
        setIsActive(res.data.isActive);
        const fromUserPartners = (res.data.userPartners || [])
          .map((p: any) => String(p.partnersId ?? p.PartnersId))
          .filter(Boolean);
        if (fromUserPartners.length > 0) {
          setSelectedMultiPartners(prev => Array.from(new Set([...prev, ...fromUserPartners])));
        }
        const partnerItems: PartnerItem[] = (res.data.userPartners || [])
          .map((p: any) => ({
            partnersId: String(p.partnersId ?? p.PartnersId ?? ''),
            customerName: p.customerName ?? p.CustomerName ?? '',
          }))
          .filter((p: PartnerItem) => !!p.partnersId);

        if (partnerItems.length > 0) {
          setpartnerData(prev => {
            const byId: Record<string, PartnerItem> = {};
            [...prev, ...partnerItems].forEach((p: PartnerItem) => { byId[p.partnersId] = p; });
            return Object.values(byId).sort((a, b) => a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' }));
          });
        }
        // Notification preferences are included in getById response; no separate API call
        if (res.data.tagAlerts !== undefined || res.data.signatureRequests !== undefined) {
          setNotificationPrefs({
            tagAlerts: !!res.data.tagAlerts,
            signatureRequests: !!res.data.signatureRequests,
            stampedDocuments: !!res.data.stampedDocuments,
            diligentSearch: !!res.data.diligentSearch,
            pastDueTransactions: res.data.pastDueTransactions !== false,
            showDiligentSearchToggle: !!res.data.showDiligentSearchToggle,
          });
        } else {
          setNotificationPrefs(null);
        }
        didHydrateRef.current = true;
      })
      .catch(() => {});
  }, [Open, UsersBL?.userId, token]);

  // Load partners; normalize keys; ensure user’s partners are present in options
  const fetchPartners = () => {
    const endpoint =
      userRolesArr.includes('Client Admin') || userRolesArr.includes('Client Processors')
        ? '/customer/getPartnersList'
        : '/Partners/getPartners';

    apiClient(endpoint, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      })
    })
      .then(responseJson => {
        if (!responseJson) return;

        const fetched: PartnerItem[] = (responseJson.data || [])
          .map((p: any) => ({
            partnersId: String(p.partnersId ?? p.PartnersId ?? ''),
            customerName: p.customerName ?? p.CustomerName ?? ''
          }))
          .filter((p: PartnerItem) => !!p.partnersId);

        const fromUser: PartnerItem[] = (UsersBL?.userPartners || [])
          .map((p: any) => ({
            partnersId: String(p.partnersId ?? p.PartnersId ?? ''),
            customerName: p.customerName ?? p.CustomerName ?? ''
          }))
          .filter((p: PartnerItem) => !!p.partnersId);

        const byId: Record<string, PartnerItem> = {};
        [...fetched, ...fromUser].forEach((p: PartnerItem) => {
          byId[p.partnersId] = p;
        });

        const sorted = Object.values(byId).sort((a, b) => a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' }));
        setpartnerData(sorted);

        const isCreate = !UsersBL?.userId || UsersBL.userId === 0;
        const isClientPortalCreator =
          userRolesArr.includes('Client Admin') || userRolesArr.includes('Client Processors');
        if (isCreate && isClientPortalCreator && sorted.length > 0) {
          setSelectedMultiPartners(sorted.map(p => p.partnersId));
        }
      })
      .catch(err => console.error('Error fetching partners:', err));
  };

  // 4) Refresh partners list whenever dialog opens
  useEffect(() => {
    if (Open) fetchPartners();
  }, [Open]);

  // 5) Notification preferences are loaded from getById response (no separate API call). Clear when opening for new user.
  useEffect(() => {
    if (Open && (!UsersBL?.userId || UsersBL.userId === 0)) {
      setNotificationPrefs(null);
    }
  }, [Open, UsersBL?.userId]);

  // Roles
  const fetchRoles = () => {
    apiClient('/Users/getAllRoles', {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      })
    })
      .then(res => {
        if (!res) return;
        
        const filtered: RoleItem[] = res.data.filter((r: RoleItem) => r.isCustomerRole === true)
          .filter((role: RoleItem) => ClientUserRoles.includes(role.roleName));
        setRoles(filtered);
      })
      .catch(err => console.error('Error fetching roles:', err));
  };
  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ClientUserRoles]);

  const handleBoxClose = () => {
    setSelectedMultiPartners([]);
    handleClose();
  };

  const FMSelectMultiPartnersCustom: React.FC<{ name: string }> = () => {
    const handlePartnerChange = (e: any) => {
      const value = e.target.value;
      const selectedValues: string[] = Array.isArray(value) ? value : String(value).split(',');
      setSelectedMultiPartners(selectedValues);
    };

    const handlePartnerRemove = (valueToRemove: string) => {
      setSelectedMultiPartners(prev => prev.filter(id => id !== valueToRemove));
    };

    return (
      <FormControl fullWidth>
        <FormHelperText className="formtexthelper-heading">Customers</FormHelperText>
        <Select
          label={null}
          multiple
          labelId="partners-select-label"
          IconComponent={ExpandMoreIcon}
          disabled={partnersDisabled}
          value={selectedMultiPartners}
          onChange={handlePartnerChange}
          sx={selectFieldSx}
          renderValue={() => (
            <Stack gap={1} direction="row" flexWrap="wrap">
              {selectedMultiPartners.map((value: string, index: number) => {
                const partnerName = selectedPartnerNames[index] || value;
                return (
                  <Chip
                    key={value}
                    label={partnerName}
                    onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    onDelete={partnersDisabled ? undefined : () => handlePartnerRemove(value)}
                    deleteIcon={
                      partnersDisabled ? undefined : (
                        <CancelIcon onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }} />
                      )
                    }
                  />
                );
              })}
            </Stack>
          )}
        >
          {Partner.map(p => (
            <MenuItem key={p.partnersId} value={p.partnersId}>
              {p.customerName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const dialogTitle = initialValues.userId === 0 ? 'Create Client User' : 'Edit Client User';

  return (
    <Dialog
      open={Open}
      onClose={handleBoxClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          width: '90%',
          maxWidth: 900,
          minHeight: '50vh',
          maxHeight: '90vh',
          borderRadius: 2,
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
        },
      }}
      sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
    >
      {loading && <LinearProgress sx={{ flexShrink: 0 }} />}
      <DialogTitle
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          py: 2,
          px: 3,
          background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
          color: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonOutlineOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
          <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
            {dialogTitle}
          </Typography>
        </Box>
        <IconButton
          onClick={handleBoxClose}
          sx={{
            color: 'rgba(255,255,255,0.9)',
            '&:hover': {
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.12)',
            },
          }}
          aria-label="Close dialog"
        >
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validateOnChange
        validationSchema={UsersModel_VS}
        onSubmit={async (data, { setSubmitting, setErrors, setFieldValue }) => {
          try {
            let selectedRoleIds = data.roleIds?.split(',').filter(Boolean) || [];
            // If isDiligentUser is false, remove "Diligent Search User" role if it's selected
            if (!isDiligentPartner && selectedRoleIds.length > 0) {
              const diligentRole = Roles.find(r => r.roleName === 'Diligent Search User');
              if (diligentRole && selectedRoleIds.includes(String(diligentRole.roleId))) {
                selectedRoleIds = selectedRoleIds.filter(id => id !== String(diligentRole.roleId));
                // Update formik field value to reflect the change
                setFieldValue('roleIds', selectedRoleIds.join(','));
              }
            }
            if (!selectedRoleIds || selectedRoleIds.length < 1) {
              setErrors({ roleIds: 'Please select Role' } as any);
              setSubmitting(false);
              return;
            }

            const isClientUser =
              userRolesArr.includes('Client Admin') || userRolesArr.includes('Client Processors');

            if (selectedMultiPartners.length === 0 && !isClientUser) {
              alert('Customer required');
              setSubmitting(false);
              return;
            }

            let finalPartnerIds = selectedMultiPartners;
            if (isClientUser && selectedMultiPartners.length === 0) {
              finalPartnerIds = Partner.map(p => p.partnersId);
            }

            const partnerIdsNumeric = finalPartnerIds
              .map(id => Number(id))
              .filter(n => !Number.isNaN(n) && n > 0);
            const creatorPartnersId = user?.partnersId != null ? Number(user.partnersId) : 0;
            const useCreatorPrimary =
              isClientUser &&
              partnerIdsNumeric.length > 1 &&
              creatorPartnersId > 0 &&
              partnerIdsNumeric.includes(creatorPartnersId);
            const primaryPartnerId = useCreatorPrimary
              ? creatorPartnersId
              : partnerIdsNumeric.length
                ? partnerIdsNumeric[0]
                : 0;

            const requestData = {
              ...data,
              roleIds: selectedRoleIds.join(', '),
              isSIPAccess: SipAccess,
              isActive: isActive,
              partnersId: primaryPartnerId, // TODO: Investigate the duplication between user.PartnersId and the UserPartners table.
              MultiPartnersIds: finalPartnerIds,
              partnersIds: finalPartnerIds.join(', '), // Saved to UserPartners and helps identify diligent users
                                                       // by checking that all partners are in the VenGroup.
              customerName: finalPartnerIds
                .map(id => Partner.find(p => p.partnersId === id)?.customerName)
                .filter(Boolean)
                .join(', ') // optional helper
            };

            setloading(true);

            const response = await apiClient('/Users/AddUpdate', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify(requestData)
            });

            const isCreate = (initialValues.userId ?? 0) === 0;
            if (response && response.resp !== 1) {
              handleSubmit();
              await Swal.fire({
                title: isCreate ? 'User added successfully' : 'User updated successfully',
                text: '',
                icon: 'success',
                confirmButtonText: 'OK'
              });
              // Clear role selection only after a successful save
              setSelectedOption([]);
              setSelectedMultiPartners([]);
              handleBoxClose();
            } else {
              const backendMessageRaw = String(response?.message ?? '').trim();
              const backendMessage = backendMessageRaw.toLowerCase();
              let warnMsg = backendMessageRaw || 'Unable to save user. Please try again.';
              if (backendMessage.includes('username')) {
                warnMsg = 'Username already exists';
              } else if (backendMessage.includes('email')) {
                warnMsg = 'Email already exists';
              }
              await Swal.fire({
                title: warnMsg,
                text: '',
                icon: 'warning',
                confirmButtonText: 'OK'
              });
            }
          } catch (error) {
            console.error('Error submitting form:', error);
            alert('An error occurred while saving the data');
          } finally {
            setSubmitting(false);
            setloading(false);
          }
        }}
      >
        {({ isSubmitting, values }) => {
          const selectedRoleIds = String(values.roleIds || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean);
          const selectedRoleNames = selectedRoleIds
            .map((id) => Roles.find((r) => String(r.roleId) === id)?.roleName)
            .filter(Boolean) as string[];
          const hasDiligentSearchRoleSelected = selectedRoleNames.includes('Diligent Search User');
          const hasClientAdminRoleSelected = selectedRoleNames.includes('Client Admin');
          const hasAtLeastOneDiligentClientSelected =
            selectedPartnerNames.some(name => diligentSearchPartnerList.some(dsp => dsp.customerName === name));

          const shouldShowDiligentSearchNotification = hasAtLeastOneDiligentClientSelected && (hasClientAdminRoleSelected || hasDiligentSearchRoleSelected);
          const isDiligentSearchNotificationOn = !!notificationPrefs?.diligentSearch;
          const isCurrentDiligentSearchOnlyUser =
            selectedRoleNames.length === 1 && selectedRoleNames[0] === 'Diligent Search User';
          const showSipAccess = isSuperAdmin;
          const showCustomers = isSuperAdmin;
          return (
            <Form className="main-form overflow-auto">
              <DialogContent sx={{ p: '0px' , '& > .MuiGrid-root': { px: { xs: 2, sm: 3 }, py: 3 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="overline" sx={sectionTitleSx}>User details</Typography>
                    <Paper variant="outlined" sx={{...paperSectionSx, mb: 2}}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">First Name</FormHelperText>
                            <FMTextField name="firstName" autoFocus margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{ ...textFieldSx, width: '100%' }} />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">Last Name</FormHelperText>
                            <FMTextField name="lastName" margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{ ...textFieldSx, width: '100%' }} />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">Username</FormHelperText>
                            <FMTextField name="userName" autoComplete="off" margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{ ...textFieldSx, width: '100%' }} />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl sx={{ width: '100%' }}>
                            <FormHelperText className="formtexthelper-heading">Email</FormHelperText>
                            <FMTextField name="userEmail" margin="dense" label={null} type="email" fullWidth variant="outlined" sx={{ ...textFieldSx, width: '100%' }} />
                          </FormControl>
                        </Grid>
                        {showCustomers && (
                          <Grid item xs={12} sm={6}>
                            <FormControl sx={{ width: '100%' }}>
                              <FMSelectMultiPartnersCustom name="partnersIds" />
                            </FormControl>
                          </Grid>
                        )}
                        <Grid item xs={12} sm={12}>
                          <FormHelperText className="formtexthelper-heading">Roles</FormHelperText>
                          <FormControl sx={{ width: '100%' }}>
                            <FMSelectRolesCustomcopy name="roleIds" UsersBL={UsersBL} Roles={Roles} isDiligent={isDiligentPartner} />
                          </FormControl>
                        </Grid>
                        {showSipAccess && (
                          <Grid item xs={12} sm={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={SipAccess}
                                  onChange={e => SetSipAccess(e.target.checked)}
                                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: PrimaryBlueColor }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PrimaryBlueColor } }}
                                />
                              }
                              label="SIP Access"
                              sx={{ color: theme.palette.text.primary }}
                            />
                          </Grid>
                        )}
                        
                        {initialValues.userId > 0 && notificationPrefs && (
                          <Grid item xs={12}>
                      <FormHelperText
                        className="formtexthelper-heading"
                        sx={{ mt: 1, color: theme.palette.text.secondary }}
                      >
                        Email notification preferences (read-only)
                      </FormHelperText>
                      <Box
                        sx={{
                          pl: 1,
                          pt: 0.5,
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {!isCurrentDiligentSearchOnlyUser && (
                          <>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                              Tag Alerts: {notificationPrefs.tagAlerts ? 'On' : 'Off'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                              Signature Requests: {notificationPrefs.signatureRequests ? 'On' : 'Off'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                              Stamped Documents: {notificationPrefs.stampedDocuments ? 'On' : 'Off'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                              Past Due Transactions: {notificationPrefs.pastDueTransactions ? 'On' : 'Off'}
                            </Typography>
                          </>
                        )}
                        {shouldShowDiligentSearchNotification && (
                          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                            Diligent Search: {isDiligentSearchNotificationOn ? 'On' : 'Off'}
                          </Typography>
                        )}
                      </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                    <Typography variant="overline" sx={sectionTitleSx}>User Access and Permissions</Typography>
                    <Paper variant="outlined" sx={paperSectionSx}>
                      {(userRolesArr.includes('Super Admin') || userRolesArr.includes('Customer Admin') || userRolesArr.includes('Client Admin')) && (
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isActive}
                                onChange={e => setIsActive(e.target.checked)}
                                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: PrimaryBlueColor }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PrimaryBlueColor } }}
                              />
                            }
                            label={isActive ? 'Active' : 'Inactive'}
                            sx={{ color: theme.palette.text.primary }}
                          />
                        </Grid>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions
                sx={{
                  px: 3,
                  py: 2,
                  gap: 1.5,
                  borderTop: 1,
                  borderColor: 'divider',
                  backgroundColor: 'grey.50',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={handleBoxClose}
                  startIcon={<CloseOutlinedIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#e2e8f0',
                    color: '#475569',
                    '&:hover': {
                      borderColor: '#2099d2',
                      backgroundColor: '#2099d214',
                      color: '#2099d2',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  type="submit"
                  disabled={isSubmitting}
                  startIcon={<SaveOutlinedIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#2099d2',
                    '&:hover': { backgroundColor: '#176990' },
                    px: 2.5,
                  }}
                >
                  Submit
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};